/**
 * Browser Hyperliquid Client
 * Direct SDK wrapper replacing Electron IPC calls
 */
import { HttpTransport, InfoClient, ExchangeClient } from '@nktkas/hyperliquid';
import { Wallet } from 'ethers';
import { DualInfoClient } from '../../utils/dual-info-client.js';

export class BrowserHyperliquidClient {
    constructor() {
        this.exchClient = null;         // Primary: order placement
        this.exchClientCancel = null;   // Secondary: order cancellation (dual key mode)
        this.infoClient = null;
        this.wsClient = null;
        this.wallet = null;
        this.walletCancel = null;       // Secondary wallet for cancels
        this.isInitialized = false;
        this.isDualKeyMode = false;     // True when using separate keys for orders/cancels
        this.transport = null;
        this.privateKey = null;
        this.privateKeyCancel = null;   // Secondary key for cancels
        this.publicAddress = null;  // Stored separately from derived address
        this.assetSzDecimals = undefined;  // Cached szDecimals for current asset
        this.priceDecimals = undefined;    // Cached price decimals for current asset
        this.builderAddress = undefined;   // Cached builder address for HIP-3 orders

        // === CACHING TO PREVENT RATE LIMITS ===
        // Asset indices rarely change - cache them to avoid repeated API calls
        this.assetIndexCache = new Map();      // symbol → {index, szDecimals, priceDecimals}
        this.perpDexsCache = null;             // Cached perpDexs() result
        this.metaCache = new Map();            // dexParam → meta
        this._spotMetaCache = null;            // Cached spotMeta() result for spot trading
        
        // Remote node configuration
        this.remoteNodeUrl = null;
        this.remoteTransport = null;
        this.remoteInfoClient = null;
        this.useRemoteNode = false;
    }

    /**
     * Initialize client with private key
     * Replaces: window.electronAPI.invoke('hyperliquid:initialize', apiKey, publicAddress)
     */
    async initialize(privateKey, publicAddress = null, isTestnet = false, remoteNodeUrl = null) {
        try {
            // console.log('🔧 Initializing Hyperliquid client in browser...');
            
            // Create wallet from private key
            this.wallet = new Wallet(privateKey);
            this.privateKey = privateKey;
            
            // Store public address separately (user's actual trading address)
            this.publicAddress = publicAddress || this.wallet.address;
            
            // console.log('📍 Using address for API calls:', this.publicAddress);
            // console.log('📍 Wallet derived address:', this.wallet.address);
            
            // Create PUBLIC API transport (for trading + historical data)
            this.transport = new HttpTransport({ isTestnet });
            
            // Create public InfoClient
            const publicInfoClient = new InfoClient({ transport: this.transport });
            
            // Create REMOTE NODE transport if URL provided
            if (remoteNodeUrl) {
                this.remoteNodeUrl = remoteNodeUrl;
                this.remoteTransport = new HttpTransport({
                    isTestnet,
                    server: {
                        mainnet: { api: remoteNodeUrl },
                        testnet: { api: remoteNodeUrl }
                    },
                    timeout: 10000
                });
                this.remoteInfoClient = new InfoClient({ transport: this.remoteTransport });
                this.useRemoteNode = true;
                // console.log('✅ [HYPERLIQUID-CLIENT] Remote node enabled:', remoteNodeUrl);
            }
            
            // Wrap in DualInfoClient for transparent routing
            this.infoClient = new DualInfoClient(
                publicInfoClient,
                this.remoteInfoClient,
                this.useRemoteNode
            );
            
            // Initialize Exchange Client (ALWAYS uses public API for trading)
            this.exchClient = new ExchangeClient({
                wallet: this.wallet,
                transport: this.transport
            });
            
            // WebSocket client will be initialized when needed
            this.wsClient = null;
            
            this.isInitialized = true;

            // console.log('✅ Hyperliquid client initialized');
            // console.log('   Wallet:', this.wallet.address);

            return true;
        } catch (error) {
            // console.error('❌ Failed to initialize Hyperliquid client:', error);
            throw error;
        }
    }

    /**
     * Initialize client with DUAL API keys (like mm4 Python workers)
     * - primaryKey: For order placement (like mm-hyperliquid.py)
     * - cancelKey: For order cancellation (like mm-hyperliquid-c.py)
     *
     * This mirrors the Python worker architecture where separate keys are used
     * to avoid nonce conflicts during high-frequency trading.
     */
    async initializeDualKey(primaryKey, cancelKey, publicAddress = null, isTestnet = false, remoteNodeUrl = null) {
        try {
            // console.log('🔧 Initializing Hyperliquid client with DUAL KEYS...');

            // Create primary wallet for orders
            this.wallet = new Wallet(primaryKey);
            this.privateKey = primaryKey;

            // Create secondary wallet for cancels
            this.walletCancel = new Wallet(cancelKey);
            this.privateKeyCancel = cancelKey;

            // Store public address (should be same for both keys - same account)
            this.publicAddress = publicAddress || this.wallet.address;

            // console.log('📍 Trading address:', this.publicAddress);
            // console.log('📍 Order key wallet:', this.wallet.address);
            // console.log('📍 Cancel key wallet:', this.walletCancel.address);

            // Create PUBLIC API transport (shared)
            this.transport = new HttpTransport({ isTestnet });
            
            // Create public InfoClient
            const publicInfoClient = new InfoClient({ transport: this.transport });
            
            // Create REMOTE NODE transport if URL provided
            if (remoteNodeUrl) {
                this.remoteNodeUrl = remoteNodeUrl;
                this.remoteTransport = new HttpTransport({
                    isTestnet,
                    server: {
                        mainnet: { api: remoteNodeUrl },
                        testnet: { api: remoteNodeUrl }
                    },
                    timeout: 10000
                });
                this.remoteInfoClient = new InfoClient({ transport: this.remoteTransport });
                this.useRemoteNode = true;
                // console.log('✅ [HYPERLIQUID-CLIENT] Remote node enabled:', remoteNodeUrl);
            }
            
            // Wrap in DualInfoClient for transparent routing
            this.infoClient = new DualInfoClient(
                publicInfoClient,
                this.remoteInfoClient,
                this.useRemoteNode
            );

            // Initialize Primary Exchange Client (for orders)
            this.exchClient = new ExchangeClient({
                wallet: this.wallet,
                transport: this.transport
            });

            // Initialize Secondary Exchange Client (for cancels)
            this.exchClientCancel = new ExchangeClient({
                wallet: this.walletCancel,
                transport: this.transport
            });

            this.wsClient = null;
            this.isInitialized = true;
            this.isDualKeyMode = true;

            // console.log('✅ Hyperliquid client initialized with DUAL KEYS');
            // console.log('   Order placement: KEY_1');
            // console.log('   Cancellation: KEY_2');

            return true;
        } catch (error) {
            // console.error('❌ Failed to initialize dual-key Hyperliquid client:', error);
            throw error;
        }
    }

    /**
     * Place order (Quick Trade)
     * Replaces: window.electronAPI.invoke('hyperliquid:executeQuickTrade', tradeData)
     */
    async executeQuickTrade(tradeData) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            const orderData = {
                coin: tradeData.asset,
                is_buy: tradeData.direction === 'BUY',
                sz: parseFloat(tradeData.size),
                limit_px: parseFloat(tradeData.price),
                order_type: { limit: { tif: 'Gtc' } },
                reduce_only: false
            };

            const result = await this.exchClient.order(orderData);
            
            return {
                success: true,
                data: result,
                message: 'Order placed successfully'
            };
        } catch (error) {
            // console.error('Order execution failed:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Place quick trade with TP/SL for Auto Trade Bot
     * Converts Auto Trade Bot format to Trading Terminal format
     */
    async placeQuickTradeWithTPSL(tradeData) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            // console.log('🤖 [placeQuickTradeWithTPSL] Input trade data:', tradeData);
            
            // Convert Auto Trade Bot/Quick Trade format to Trading Terminal format
            // Handle both formats:
            // 1. Auto Trade Bot: { direction: 'LONG'/'SHORT', entry, target, stop, positionSize }
            // 2. Quick Trade: { direction: 'long'/'short', entry_price, target_price, stop_price, size }
            
            const direction = (tradeData.direction || '').toUpperCase();
            const orderSide = (direction === 'LONG' || direction === 'long') ? 'buy' : 'sell';
            
            // Size: use calculated size if available, otherwise fall back to positionSize percentage
            const size = tradeData.size || tradeData.positionSize || 2.5;
            
            // Prices: handle both snake_case (quick trade) and camelCase (auto trade bot)
            const entryPrice = parseFloat(tradeData.entry_price || tradeData.entry || 0);
            const targetPrice = parseFloat(tradeData.target_price || tradeData.target || 0);
            const stopPrice = parseFloat(tradeData.stop_price || tradeData.stop || 0);
            
            // console.log('🤖 [placeQuickTradeWithTPSL] Parsed values:', {
            //     direction,
            //     orderSide,
            //     size,
            //     entryPrice,
            //     targetPrice,
            //     stopPrice
            // });
            
            const orderData = {
                asset: tradeData.asset,
                orderSide: orderSide,
                size: size,
                orderType: 'limit',
                price: entryPrice,
                tpslEnabled: true,
                tpPrice: targetPrice,
                slPrice: stopPrice,
                timeInForce: 'GTC',
                reduceOnly: false
            };

            // console.log('🤖 [placeQuickTradeWithTPSL] Converted order data:', orderData);

            // Use existing executeTradingOrder method
            const result = await this.executeTradingOrder(orderData);

            return result;

        } catch (error) {
            // console.error('❌ [placeQuickTradeWithTPSL] Order execution failed:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Place order (Trading Terminal)
     * Replaces: window.electronAPI.invoke('hyperliquid:executeTradingOrder', orderData)
     * Full implementation matching trading-order-manager.js
     */
    async executeTradingOrder(orderData) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            // Validate order data
            const validation = this.validateOrderData(orderData);
            if (!validation.isValid) {
                throw new Error(`Order validation failed: ${validation.errors.join(', ')}`);
            }

            // Update leverage if specified (before placing order)
            // console.log('🔍 [LEVERAGE-DEBUG] Raw orderData.leverage:', orderData.leverage, 'Type:', typeof orderData.leverage);
            
            // Validate leverage is a valid number
            const leverageValue = parseInt(orderData.leverage);
            const isValidLeverage = !isNaN(leverageValue) && leverageValue > 0 && leverageValue <= 100;
            
            // console.log('🔍 [LEVERAGE-DEBUG] Parsed leverage:', leverageValue, 'isValid:', isValidLeverage);
            
            if (isValidLeverage) {
                // console.log(`📊 [LEVERAGE] Updating leverage to ${leverageValue}x for ${orderData.asset}`);
                const assetIndex = await this.getAssetIndex(orderData.asset);
                // console.log('🔍 [LEVERAGE-DEBUG] Asset index:', assetIndex);
                
                // HIP-3 assets require isolated margin (cross margin not supported yet)
                const isHip3 = orderData.asset.includes(':');
                const marginMode = isHip3 ? 'ISOLATED' : 'CROSS';
                
                await this.exchClient.updateLeverage({
                    asset: assetIndex,
                    isCross: !isHip3,  // Cross for regular assets, Isolated for HIP-3
                    leverage: leverageValue
                });
                // console.log(`✅ [LEVERAGE] Successfully updated to ${leverageValue}x ${marginMode} for ${orderData.asset}`);
            } else {
                // console.warn(`⚠️ [LEVERAGE] Skipping invalid leverage value:`, orderData.leverage, '(parsed:', leverageValue, ')');
            }

            // Build SDK-compatible order structure
            const sdkOrder = await this.buildSDKOrder(orderData);
            
            // console.log(`🚀 [ORDER-SUBMIT] Submitting order to Hyperliquid...`);
            // console.log(`📋 [ORDER-SUBMIT] Asset: ${orderData.asset}, Side: ${orderData.orderSide}, Size: ${orderData.size}, Type: ${orderData.orderType}`);
            // console.log(`📋 [ORDER-SUBMIT] SDK Order:`, JSON.stringify(sdkOrder, null, 2));
            
            // Execute order via Hyperliquid SDK
            const result = await this.exchClient.order(sdkOrder);
            
            // console.log(`✅ [ORDER-RESULT] Order submitted successfully!`);
            // console.log(`📊 [ORDER-RESULT] Result:`, JSON.stringify(result, null, 2));
            
            // Track successful order
            const cloid = sdkOrder.orders[0].c;

            return {
                success: true,
                result: result,
                cloid: cloid,
                orderCount: sdkOrder.orders.length,
                timestamp: Date.now()
            };

        } catch (error) {
            // console.error('❌ [ORDER-FAILED] Trading order execution failed:', error);
            // console.error('❌ [ORDER-FAILED] Error details:', error.message);
            // console.error('❌ [ORDER-FAILED] Full error:', JSON.stringify(error, null, 2));
            return {
                success: false,
                error: error.message,
                timestamp: Date.now()
            };
        }
    }

    /**
     * Get asset index for a given asset name
     * For HIP-3 assets, returns absolute index (110000+) for correct DEX routing
     * For regular assets, returns standard index (0, 1, 2...)
     * Also caches szDecimals for rounding
     *
     * CACHING: Results are cached to prevent rate limits during high-frequency trading.
     * First call fetches from API, subsequent calls return cached value instantly.
     */
    async getAssetIndex(assetSymbol) {
        try {
            // === CHECK CACHE FIRST (prevents rate limits) ===
            const cached = this.assetIndexCache.get(assetSymbol);
            if (cached) {
                // Use cached values
                this.assetSzDecimals = cached.szDecimals;
                this.priceDecimals = cached.priceDecimals;
                return cached.index;
            }

            // Detect DEX from symbol (HIP-3 assets have ':' in name)
            let dexParam = '';
            let isHip3 = false;

            if (assetSymbol.includes(':')) {
                const prefix = assetSymbol.split(':')[0];
                dexParam = prefix; // 'xyz', 'flx', or 'vntl'
                isHip3 = true;
            }

            // === GET META (with caching) ===
            let meta = this.metaCache.get(dexParam);
            if (!meta) {
                meta = await this.infoClient.meta({ dex: dexParam });
                this.metaCache.set(dexParam, meta);
            }

            const relativeIndex = meta.universe.findIndex(asset => asset.name === assetSymbol);

            if (relativeIndex === -1) {
                throw new Error(`Asset ${assetSymbol} not found in ${dexParam || 'main'} DEX universe`);
            }

            // Cache szDecimals for this asset (used by rounding functions)
            const asset = meta.universe[relativeIndex];
            this.assetSzDecimals = parseInt(asset.szDecimals);
            this.priceDecimals = 6 - this.assetSzDecimals; // MAX_DECIMALS = 6 for perps

            // For HIP-3 assets, calculate absolute index with offset
            let absoluteIndex = relativeIndex;

            if (isHip3) {
                // === GET PERP DEXS (with caching) ===
                if (!this.perpDexsCache) {
                    this.perpDexsCache = await this.infoClient.perpDexs();
                }
                const allDexs = this.perpDexsCache;

                // Find the position of this DEX in the list (skip main DEX which is null)
                let dexPosition = -1;
                for (let i = 0; i < allDexs.length; i++) {
                    if (allDexs[i] !== null && allDexs[i].name === dexParam) {
                        // Count how many HIP-3 DEXs come before this one
                        dexPosition = allDexs.slice(0, i).filter(d => d !== null).length;
                        break;
                    }
                }

                if (dexPosition === -1) {
                    throw new Error(`DEX ${dexParam} not found in perpDexs list`);
                }

                // Calculate offset: first HIP-3 DEX = 110000, second = 120000, etc.
                const offset = 110000 + (dexPosition * 10000);
                absoluteIndex = relativeIndex + offset;
            }

            // === CACHE THE RESULT ===
            this.assetIndexCache.set(assetSymbol, {
                index: absoluteIndex,
                szDecimals: this.assetSzDecimals,
                priceDecimals: this.priceDecimals
            });

            // console.log(`[ASSET-INDEX] ${assetSymbol} → Index: ${absoluteIndex}, szDecimals: ${this.assetSzDecimals} (cached for future calls)`);

            return absoluteIndex;
        } catch (error) {
            // Fallback to common indices
            if (assetSymbol === 'ETH') return 1;
            if (assetSymbol === 'BTC') return 0;
            throw error;
        }
    }

    /**
     * Validate order data from trading terminal
     * Matches trading-order-manager.js validateOrderData()
     */
    validateOrderData(orderData) {
        const errors = [];

        // Required fields
        if (!orderData.asset) errors.push('Asset is required');
        if (!orderData.orderSide || !['buy', 'sell'].includes(orderData.orderSide)) {
            errors.push('Valid order side (buy/sell) is required');
        }
        if (!orderData.size || orderData.size <= 0) {
            errors.push('Valid order size is required');
        }
        if (!orderData.orderType || !['market', 'limit'].includes(orderData.orderType)) {
            errors.push('Valid order type is required');
        }

        // Price validation for limit orders: required UNLESS placing TP/SL only
        if (orderData.orderType === 'limit') {
            const hasValidPrice = orderData.price && !isNaN(orderData.price) && orderData.price > 0;
            const hasTpSlOrders = orderData.tpslEnabled && (orderData.tpPrice || orderData.slPrice);
            
            // Require EITHER valid price OR TP/SL orders
            if (!hasValidPrice && !hasTpSlOrders) {
                errors.push('Price required for limit orders (or enable TP/SL to add to existing position)');
            }
        }

        // TP/SL validation
        if (orderData.tpslEnabled) {
            if (orderData.tpPrice && orderData.tpPrice <= 0) {
                errors.push('Take profit price must be positive');
            }
            if (orderData.slPrice && orderData.slPrice <= 0) {
                errors.push('Stop loss price must be positive');
            }
            if (!orderData.tpPrice && !orderData.slPrice) {
                errors.push('At least one TP or SL price required when TP/SL enabled');
            }
        }

        // Time in force validation
        if (orderData.timeInForce && !['GTC', 'IOC', 'ALO'].includes(orderData.timeInForce)) {
            errors.push('Time in force must be GTC, IOC, or ALO');
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Build SDK-compatible order structure from trading terminal data
     * Matches trading-order-manager.js buildSDKOrder()
     */
    async buildSDKOrder(orderData) {
        // Get asset index (also caches szDecimals)
        const assetIndex = await this.getAssetIndex(orderData.asset);
        
        // Generate unique CLOID
        const cloid = this.generateCLOID();
        
        const orders = [];

        // Determine if this is TP/SL-only (no entry order)
        const hasValidPrice = orderData.price && !isNaN(orderData.price) && orderData.price > 0;
        const isMarketOrder = orderData.orderType === 'market';
        const isTpSlOnly = !hasValidPrice && !isMarketOrder && orderData.tpslEnabled && (orderData.tpPrice || orderData.slPrice);

        // Build main entry order if: market order OR valid price provided
        if (isMarketOrder || hasValidPrice) {
            const entryOrder = await this.buildEntryOrder(orderData, assetIndex, cloid);
            orders.push(entryOrder);
        }

        // Add TP/SL orders if enabled
        if (orderData.tpslEnabled) {
            if (orderData.tpPrice) {
                const tpOrder = await this.buildTPOrder(orderData, assetIndex, cloid);
                orders.push(tpOrder);
            }
            if (orderData.slPrice) {
                const slOrder = await this.buildSLOrder(orderData, assetIndex, cloid);
                orders.push(slOrder);
            }
        }

        // Grouping strategy:
        // - 'na': TP/SL-only (no entry) OR standalone orders
        // - 'normalTpsl': Entry + TP/SL bundle
        const grouping = isTpSlOnly ? 'na' : (orderData.tpslEnabled ? 'normalTpsl' : 'na');
        
        return {
            orders: orders,
            grouping: grouping,
            builder: {
                b: '0xC1A2f762F67aF72FD05e79afa23F8358A4d7dbaF',  // Builder address
                f: 5   // 5 tenths of bp = 0.5 bp = 0.005%
            }
        };
    }

    /**
     * Build entry order based on order type
     * Matches trading-order-manager.js buildEntryOrder()
     */
    async buildEntryOrder(orderData, assetIndex, cloid) {
        // Round size to asset's szDecimals
        const roundedSize = this.roundToSizeDecimals(orderData.size);
        if (roundedSize === null) {
            throw new Error('Invalid order size');
        }
        
        // Convert to string - this should give clean decimal representation
        const sizeString = String(roundedSize);
        // console.log(`[BUILD-ORDER] Size: ${orderData.size} → rounded: ${roundedSize} → string: "${sizeString}"`);

        const baseOrder = {
            a: assetIndex,
            b: orderData.orderSide === 'buy',
            s: sizeString,
            r: Boolean(orderData.reduceOnly),
            c: cloid
        };

        // Set order type and price based on orderType
        switch (orderData.orderType) {
            case 'market':
                // Market orders use aggressive IOC limit orders with best bid/ask
                const bidAsk = await this.getBestBidAsk(orderData.asset);
                
                // Use 5-10% slippage (standard for Hyperliquid, no MEV risk)
                const slippageBuffer = 0.10; // 10% slippage for guaranteed execution
                
                // Start from best bid/ask, not mid price
                const basePrice = orderData.orderSide === 'buy' 
                    ? bidAsk.bestAsk  // Buy at best ask (what sellers want)
                    : bidAsk.bestBid; // Sell at best bid (what buyers want)
                
                const priceWithSlippage = orderData.orderSide === 'buy' 
                    ? basePrice * (1 + slippageBuffer)  // Buy up to 10% higher
                    : basePrice * (1 - slippageBuffer); // Sell down to 10% lower
                
                // console.log(`📊 [MARKET-ORDER] ${orderData.asset} ${orderData.orderSide.toUpperCase()}`);
                // console.log(`   Best Bid: $${bidAsk.bestBid} | Best Ask: $${bidAsk.bestAsk} | Mid: $${bidAsk.mid}`);
                // console.log(`   Base Price: $${basePrice} (${orderData.orderSide === 'buy' ? 'ask' : 'bid'})`);
                // console.log(`   Order Price: $${this.roundToTickSize(priceWithSlippage)} (${slippageBuffer * 100}% slippage)`);
                
                baseOrder.p = String(this.roundToTickSize(priceWithSlippage));
                baseOrder.t = {
                    limit: {
                        tif: 'Gtc'  // GTC allows order to rest in book, fills at best price
                    }
                };
                break;
                
            case 'limit':
                const roundedPrice = this.roundToTickSize(orderData.price);
                
                if (roundedPrice === null || roundedPrice === undefined) {
                    throw new Error('Invalid price for limit order');
                }
                
                baseOrder.p = String(roundedPrice);
                baseOrder.t = {
                    limit: {
                        tif: this.mapTimeInForce(orderData.timeInForce)
                    }
                };
                break;
                
            default:
                throw new Error(`Unsupported order type: ${orderData.orderType}`);
        }

        return baseOrder;
    }

    /**
     * Build Take Profit order
     * Matches trading-order-manager.js buildTPOrder()
     */
    async buildTPOrder(orderData, assetIndex, cloid) {
        const tpPrice = parseFloat(orderData.tpPrice);
        
        // Round size to asset's szDecimals
        const roundedSize = this.roundToSizeDecimals(orderData.size);
        if (roundedSize === null) {
            throw new Error('Invalid TP order size');
        }
        
        const tpOrder = {
            a: assetIndex,
            b: orderData.orderSide !== 'buy', // Opposite direction
            p: String(this.roundToTickSize(tpPrice)),
            s: String(roundedSize),
            r: true, // Always reduce-only for TP/SL
            t: {
                trigger: {
                    isMarket: false, // Use limit orders for TP (better price execution)
                    triggerPx: String(this.roundToTickSize(tpPrice)),
                    tpsl: 'tp'
                }
            },
            c: cloid
        };

        return tpOrder;
    }

    /**
     * Build Stop Loss order
     * Matches trading-order-manager.js buildSLOrder()
     */
    async buildSLOrder(orderData, assetIndex, cloid) {
        const slPrice = parseFloat(orderData.slPrice);
        
        // Round size to asset's szDecimals
        const roundedSize = this.roundToSizeDecimals(orderData.size);
        if (roundedSize === null) {
            throw new Error('Invalid SL order size');
        }
        
        const slOrder = {
            a: assetIndex,
            b: orderData.orderSide !== 'buy', // Opposite direction
            p: String(this.roundToTickSize(slPrice)),
            s: String(roundedSize),
            r: true, // Always reduce-only for TP/SL
            t: {
                trigger: {
                    isMarket: true, // Use market orders for guaranteed execution
                    triggerPx: String(this.roundToTickSize(slPrice)),
                    tpsl: 'sl'
                }
            },
            c: cloid
        };

        return slOrder;
    }

    /**
     * Generate unique CLOID for order tracking
     * Matches trading-order-manager.js generateCLOID()
     * Uses timestamp + counter to ensure uniqueness even within same millisecond
     */
    generateCLOID() {
        // Initialize counter if not exists
        if (this._cloidCounter === undefined) {
            this._cloidCounter = 0;
            this._cloidLastMs = 0;
        }

        let now = Date.now();

        // Reset counter if we're in a new millisecond
        if (now !== this._cloidLastMs) {
            this._cloidCounter = 0;
            this._cloidLastMs = now;
        } else {
            // Same millisecond - increment counter
            this._cloidCounter++;

            // Safety: If counter exceeds 0xFFFF (65535), wait for next millisecond
            // This is extremely unlikely but prevents collisions
            if (this._cloidCounter > 0xFFFF) {
                while (Date.now() === this._cloidLastMs) {
                    // Spin wait (sub-ms)
                }
                now = Date.now();
                this._cloidCounter = 0;
                this._cloidLastMs = now;
            }
        }

        // Combine timestamp (upper bits) + counter (lower bits) for uniqueness
        // Format: 0x[timestamp_hex][counter_hex] padded to 32 chars
        const timestampHex = now.toString(16);
        const counterHex = this._cloidCounter.toString(16).padStart(4, '0');
        return '0x' + (timestampHex + counterHex).padStart(32, '0');
    }

    /**
     * Get current market price for an asset (mid price)
     * Matches trading-order-manager.js getCurrentMarketPrice()
     */
    async getCurrentMarketPrice(asset) {
        try {
            // Try WebSocket first (instant, cached)
            if (window.BrowserPriceService && window.BrowserPriceService.isReady()) {
                const wsPrice = window.BrowserPriceService.getPrice(asset);
                if (wsPrice) {
                    const marketPrice = parseFloat(wsPrice);
                    const roundedPrice = this.roundToTickSize(marketPrice);
                    return roundedPrice;
                }
            }
            
            // Fallback to API
            const allMids = await this.infoClient.allMids();
            
            // Direct lookup by asset symbol
            if (allMids[asset]) {
                const marketPrice = parseFloat(allMids[asset]);
                const roundedPrice = this.roundToTickSize(marketPrice);
                return roundedPrice;
            }
            
            // For HIP-3 assets, get price from orderbook (not in allMids)
            if (asset.includes(':')) {
                // console.log(`[HIP-3] Getting market price for ${asset} from orderbook...`);
                try {
                    const l2 = await this.infoClient.l2Book({ coin: asset });
                    const bids = l2.levels[0] || [];
                    const asks = l2.levels[1] || [];
                    
                    if (bids.length > 0 && asks.length > 0) {
                        const midPrice = (parseFloat(bids[0].px) + parseFloat(asks[0].px)) / 2;
                        const roundedPrice = this.roundToTickSize(midPrice);
                        // console.log(`[HIP-3] Market price for ${asset}: $${roundedPrice}`);
                        return roundedPrice;
                    }
                    
                    throw new Error(`No orderbook data for ${asset}`);
                } catch (l2Error) {
                    // console.error(`[HIP-3] Failed to get orderbook for ${asset}:`, l2Error);
                    throw new Error(`Market price not available for ${asset}`);
                }
            }
            
            throw new Error(`Market price not found for ${asset}`);
        } catch (error) {
            // console.error(`Error getting market price for ${asset}:`, error);
            throw error;
        }
    }

    /**
     * Get best bid/ask prices for market orders
     * Returns the actual orderbook prices for aggressive execution
     */
    async getBestBidAsk(asset) {
        try {
            // Try WebSocket first (instant, cached)
            if (window.BrowserPriceService && window.BrowserPriceService.isReady()) {
                const wsPrice = window.BrowserPriceService.getPrice(asset);
                if (wsPrice) {
                    const midPrice = parseFloat(wsPrice);
                    // Approximate bid/ask with small spread (0.01% each side)
                    return {
                        bestBid: this.roundToTickSize(midPrice * 0.9999),
                        bestAsk: this.roundToTickSize(midPrice * 1.0001),
                        mid: this.roundToTickSize(midPrice)
                    };
                }
            }
            
            // Fallback to API
            // For standard assets, use allMids as approximation (no direct L2 access for all assets)
            const allMids = await this.infoClient.allMids();
            
            if (allMids[asset]) {
                const midPrice = parseFloat(allMids[asset]);
                // Approximate bid/ask with small spread (0.01% each side)
                return {
                    bestBid: this.roundToTickSize(midPrice * 0.9999),
                    bestAsk: this.roundToTickSize(midPrice * 1.0001),
                    mid: this.roundToTickSize(midPrice)
                };
            }
            
            // For HIP-3 assets, get actual orderbook
            if (asset.includes(':')) {
                const l2 = await this.infoClient.l2Book({ coin: asset });
                const bids = l2.levels[0] || [];
                const asks = l2.levels[1] || [];
                
                if (bids.length > 0 && asks.length > 0) {
                    const bestBid = parseFloat(bids[0].px);
                    const bestAsk = parseFloat(asks[0].px);
                    const mid = (bestBid + bestAsk) / 2;
                    
                    return {
                        bestBid: this.roundToTickSize(bestBid),
                        bestAsk: this.roundToTickSize(bestAsk),
                        mid: this.roundToTickSize(mid)
                    };
                }
                
                throw new Error(`No orderbook data for ${asset}`);
            }
            
            throw new Error(`Market price not found for ${asset}`);
        } catch (error) {
            // console.error(`Error getting best bid/ask for ${asset}:`, error);
            throw error;
        }
    }

    /**
     * Map trading terminal timeInForce to SDK format
     * Matches trading-order-manager.js mapTimeInForce()
     */
    mapTimeInForce(timeInForce) {
        switch (timeInForce) {
            case 'GTC':
                return 'Gtc';
            case 'IOC':
                return 'Ioc';
            case 'ALO':
            case 'postOnly':
                return 'Alo';
            default:
                return 'Gtc';
        }
    }

    /**
     * Round price to proper tick size based on asset's price range
     * Matches hyperliquid-service.js roundToTickSize()
     */
    roundToTickSize(price) {
        if (price === null || price === undefined || isNaN(price)) {
            return null;
        }
        
        // Convert to number if string
        const numPrice = typeof price === 'string' ? parseFloat(price) : price;
        
        if (isNaN(numPrice) || numPrice <= 0) {
            return null;
        }
        
        // Use Hyperliquid's actual rules: MAX_DECIMALS - szDecimals
        // For perps: MAX_DECIMALS = 6
        const maxDecimals = 6;
        const priceDecimals = this.assetSzDecimals !== undefined ? 
            maxDecimals - this.assetSzDecimals : 2; // Safe fallback
        
        // Apply Hyperliquid's 5 significant figures rule
        const significantFigures = 5;
        const magnitude = Math.floor(Math.log10(Math.abs(numPrice)));
        const maxDecimalPlaces = Math.max(0, Math.min(priceDecimals, significantFigures - magnitude - 1));
        
        // Round to the calculated decimal places
        const multiplier = Math.pow(10, maxDecimalPlaces);
        const result = Math.round(numPrice * multiplier) / multiplier;
        
        return result;
    }

    /**
     * Round size to szDecimals for the asset
     * Matches hyperliquid-service.js roundToSizeDecimals()
     */
    roundToSizeDecimals(size) {
        if (size === null || size === undefined || isNaN(size)) {
            // console.warn(`[SIZE-ROUND] Invalid size input:`, size);
            return null;
        }
        
        // Convert to number if string
        const numSize = typeof size === 'string' ? parseFloat(size) : size;
        
        if (isNaN(numSize) || numSize <= 0) {
            // console.warn(`[SIZE-ROUND] Invalid numSize:`, numSize);
            return null;
        }
        
        // Use szDecimals from asset metadata, with safe fallback
        const decimals = this.assetSzDecimals !== undefined ? this.assetSzDecimals : 5;
        
        // console.log(`[SIZE-ROUND] Input: ${numSize}, szDecimals: ${decimals} (from assetSzDecimals: ${this.assetSzDecimals})`);
        
        // Handle edge cases for very small or very large numbers
        if (numSize < 1e-15) {
            return 0;
        }
        
        if (numSize > 1e15) {
            const scientific = numSize.toExponential();
            const rounded = parseFloat(parseFloat(scientific).toFixed(decimals));
            // console.log(`[SIZE-ROUND] Large number: ${numSize} → ${rounded}`);
            return rounded;
        }
        
        // Standard rounding for normal range numbers
        const result = parseFloat(numSize.toFixed(decimals));
        
        // Remove trailing zeros by converting back to number
        const finalResult = parseFloat(result.toString());
        
        // console.log(`[SIZE-ROUND] Result: ${numSize} → ${finalResult} (${decimals} decimals)`);
        
        return finalResult;
    }

    /**
     * Cancel order - supports both OID and CLOID
     * Replaces: window.electronAPI.invoke('hyperliquid:cancelOrder', { orderId, asset })
     * Matches Electron's order-cancel-service.js logic
     *
     * In dual key mode, uses exchClientCancel (KEY_2) for cancellations
     */
    async cancelOrder({ orderId, asset }) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            // Get asset index (required for cancellation)
            const assetIndex = await this.getAssetIndex(asset);
            // console.log(`[BROWSER-CLIENT] Canceling order ${orderId} for ${asset} (index: ${assetIndex})`);

            // Use cancel client in dual key mode, otherwise use primary client
            const cancelClient = this.isDualKeyMode && this.exchClientCancel
                ? this.exchClientCancel
                : this.exchClient;

            let result;

            // Convert orderId to string for checking
            const orderIdStr = String(orderId);

            // Check if CLOID (starts with 0x) or regular OID
            if (orderIdStr.startsWith('0x')) {
                // Cancel by CLOID
                // console.log(`[BROWSER-CLIENT] Using cancelByCloid for ${orderId}`);
                result = await cancelClient.cancelByCloid({
                    cancels: [{
                        asset: assetIndex,
                        cloid: orderId
                    }]
                });
            } else {
                // Cancel by regular OID
                // console.log(`[BROWSER-CLIENT] Using cancel for OID ${orderId}`);
                result = await cancelClient.cancel({
                    cancels: [{
                        a: assetIndex,
                        o: parseInt(orderId)
                    }]
                });
            }

            const success = result?.response?.data?.statuses?.[0] === 'success';
            // console.log(`[BROWSER-CLIENT] Cancel result:`, result);

            return {
                success: success,
                data: result,
                message: success ? 'Order cancelled successfully' : 'Order cancellation failed'
            };
        } catch (error) {
            // console.error('[BROWSER-CLIENT] Order cancellation failed:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Close position
     * Replaces: window.electronAPI.invoke('hyperliquid:closePosition', { positionId, asset })
     * Matches Electron's position-close-service.js logic
     */
    async closePosition({ asset }) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            // console.log(`[BROWSER-CLIENT] Closing position for ${asset}...`);
            
            // Get current position to determine size and side
            // For HIP-3 assets, query the correct DEX
            const dexName = asset.includes(':') ? asset.split(':')[0] : '';
            const userState = await this.getUserStateForDex(dexName);
            const position = userState.assetPositions.find(p => p.position.coin === asset);
            
            if (!position) {
                throw new Error('No position found for ' + asset);
            }

            // console.log(`[BROWSER-CLIENT] Position data:`, position);
            const positionSize = parseFloat(position.position.szi);
            const isLong = positionSize > 0;
            // console.log(`[BROWSER-CLIENT] Position size: ${positionSize}, isLong: ${isLong}, will place ${isLong ? 'SELL' : 'BUY'} order to close`);

            // Get asset index (required for order placement)
            const assetIndex = await this.getAssetIndex(asset);

            // Get current market price for aggressive execution
            const marketPrice = await this.getCurrentMarketPrice(asset);
            
            if (!marketPrice) {
                throw new Error('Failed to get current market price for ' + asset);
            }

            // Determine order side (opposite of position)
            const orderSide = !isLong; // false = sell, true = buy
            const orderSize = Math.abs(positionSize);

            // Use aggressive IOC order with 10% slippage for guaranteed execution
            const aggressiveSlippage = 0.10;
            const aggressivePrice = orderSide 
                ? marketPrice * (1 + aggressiveSlippage)  // Buy 10% higher
                : marketPrice * (1 - aggressiveSlippage); // Sell 10% lower

            // Round price using proper tick size (respects asset's szDecimals)
            const roundedPrice = this.roundToTickSize(aggressivePrice);

            // Round size using proper size decimals (respects asset's szDecimals)
            const roundedSize = this.roundToSizeDecimals(orderSize);

            // console.log(`[BROWSER-CLIENT] Placing close order: side=${orderSide ? 'BUY' : 'SELL'}, size=${roundedSize}, price=${roundedPrice}`);

            // Place closing order using correct SDK format
            const result = await this.exchClient.order({
                orders: [{
                    a: assetIndex,                  // Asset index
                    b: orderSide,                   // Boolean: false = sell, true = buy
                    p: roundedPrice.toString(),     // Price as string (rounded)
                    s: roundedSize.toString(),      // Size as string (rounded)
                    r: true,                        // reduce-only
                    t: {
                        limit: {
                            tif: "Ioc"              // Immediate or cancel
                        }
                    }
                }],
                grouping: "na",
                builder: {
                    b: '0xC1A2f762F67aF72FD05e79afa23F8358A4d7dbaF',
                    f: 20
                }
            });

            // console.log(`[BROWSER-CLIENT] Close order result:`, result);

            const success = result?.response?.data?.statuses?.[0]?.filled;
            
            return {
                success: success,
                data: result,
                message: success ? 'Position closed successfully' : 'Position close failed'
            };
        } catch (error) {
            // Suppress "would increase position" error - happens after successful fill
            // console.error('[BROWSER-CLIENT] Position close failed:', error);
            // Return success anyway if it's the race condition error
            if (error.message?.includes('would increase position')) {
                return {
                    success: true,
                    message: 'Position closed successfully'
                };
            }
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Close single position with limit order (progressive slippage)
     * Uses same progressive slippage as closeMultiplePositionsLimit
     * @param {Object} params - { asset }
     * @returns {Promise<Object>} - { success, data, message }
     */
    async closePositionLimit({ asset }) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }
        
        try {
            // console.log(`[BROWSER-CLIENT] Closing position for ${asset} with limit order...`);
            
            // Get current position
            // For HIP-3 assets, query the correct DEX
            const dexName = asset.includes(':') ? asset.split(':')[0] : '';
            const userState = await this.getUserStateForDex(dexName);
            const position = userState.assetPositions.find(p => p.position.coin === asset);
            
            if (!position) {
                throw new Error('No position found for ' + asset);
            }
            
            const positionSize = parseFloat(position.position.szi);
            
            // Use closeMultiplePositionsLimit with single position
            const positionData = [{
                asset: asset,
                size: Math.abs(positionSize),
                side: positionSize > 0 ? 'LONG' : 'SHORT',
                szi: position.position.szi
            }];
            
            const result = await this.closeMultiplePositionsLimit(positionData);
            
            return {
                success: result.success,
                data: result,
                message: result.success ? `Position closed with limit orders (${result.successCount}/${result.totalPositions})` : result.error
            };
        } catch (error) {
            // console.error('[BROWSER-CLIENT] Close position limit error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Cancel multiple orders in batch (optimized for rate limits)
     * Strategy: Cancel entry orders first (auto-cancels their TP/SL), then clean up orphaned reduce-only orders
     */
    async cancelMultipleOrders(orders) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            if (!orders || orders.length === 0) {
                return { success: false, error: 'No orders provided' };
            }

            // VALIDATION: Catch wrong property names early (common bug source)
            const badOrder = orders.find(o => o.oid !== undefined || o.coin !== undefined);
            if (badOrder) {
                const msg = `cancelMultipleOrders: WRONG PROPERTY NAMES. Use {orderId, asset}, not {oid, coin}. Got: ${JSON.stringify(badOrder)}`;
                // console.error(`[BROWSER-CLIENT] ${msg}`);
                throw new Error(msg);
            }

            // console.log(`[BROWSER-CLIENT] Batch cancel: Processing ${orders.length} orders...`);

            // STEP 1: Separate entry orders from reduce-only orders
            const entryOrders = orders.filter(o => !o.reduceOnly);
            const reduceOnlyOrders = orders.filter(o => o.reduceOnly);
            
            // console.log(`[BROWSER-CLIENT] Order breakdown: ${entryOrders.length} entry, ${reduceOnlyOrders.length} reduce-only`);

            let successCount = 0;
            let failCount = 0;
            let autoCancelledCount = 0;

            // STEP 2: Cancel entry orders first (this auto-cancels their grouped TP/SL)
            if (entryOrders.length > 0) {
                // console.log(`[BROWSER-CLIENT] Step 1: Canceling ${entryOrders.length} entry orders (will auto-cancel grouped TP/SL)...`);
                
                const entryResult = await this.cancelOrdersBatch(entryOrders);
                successCount += entryResult.successCount;
                failCount += entryResult.failCount;
                
                autoCancelledCount = entryResult.successCount * 2;
            }

            // STEP 3: (REMOVED 300ms delay - user request)
            // Auto-cancellations should be synchronous on exchange side

            // STEP 4: Cancel remaining reduce-only orders (orphaned ones)
            if (reduceOnlyOrders.length > 0) {
                // console.log(`[BROWSER-CLIENT] Step 2: Cleaning up ${reduceOnlyOrders.length} reduce-only orders (orphaned)...`);
                
                const reduceOnlyResult = await this.cancelOrdersBatch(reduceOnlyOrders);
                successCount += reduceOnlyResult.successCount;
                failCount += reduceOnlyResult.failCount;
            }

            const totalCancelled = successCount + Math.min(autoCancelledCount, reduceOnlyOrders.length);
            // console.log(`[BROWSER-CLIENT] Batch cancel complete: ${successCount} explicit + ~${autoCancelledCount} auto-cancelled = ~${totalCancelled} total`);

            return {
                success: successCount > 0,
                successCount,
                failCount,
                autoCancelledCount,
                totalOrders: orders.length,
                message: `Cancelled ${successCount} orders (${autoCancelledCount} auto-cancelled with entries)`
            };
        } catch (error) {
            // console.error('[BROWSER-CLIENT] Batch cancel error:', error);
            return {
                success: false,
                error: error.message,
                successCount: 0,
                failCount: orders.length
            };
        }
    }

    /**
     * Internal method to cancel a batch of orders
     * In dual key mode, uses exchClientCancel (KEY_2) for cancellations
     */
    async cancelOrdersBatch(orders) {
        const ordersByAsset = {};
        for (const order of orders) {
            if (!ordersByAsset[order.asset]) {
                ordersByAsset[order.asset] = [];
            }
            ordersByAsset[order.asset].push(order);
        }

        // Use cancel client in dual key mode, otherwise use primary client
        const cancelClient = this.isDualKeyMode && this.exchClientCancel
            ? this.exchClientCancel
            : this.exchClient;

        let successCount = 0;
        let failCount = 0;

        for (const [asset, assetOrders] of Object.entries(ordersByAsset)) {
            try {
                const assetIndex = await this.getAssetIndex(asset);
                if (assetIndex === null) {
                    // console.error(`[BROWSER-CLIENT] Failed to get asset index for ${asset}`);
                    failCount += assetOrders.length;
                    continue;
                }

                const cloidOrders = assetOrders.filter(o => o.orderId.startsWith('0x'));
                const oidOrders = assetOrders.filter(o => !o.orderId.startsWith('0x'));

                if (cloidOrders.length > 0) {
                    const result = await cancelClient.cancelByCloid({
                        cancels: cloidOrders.map(o => ({
                            asset: assetIndex,
                            cloid: o.orderId
                        }))
                    });

                    const statuses = result?.response?.data?.statuses || [];
                    const expectedCount = cloidOrders.length;

                    // Validate response
                    if (statuses.length !== expectedCount) {
                        // console.warn(`[BROWSER-CLIENT] Status count mismatch: expected ${expectedCount}, got ${statuses.length}`);
                    }

                    statuses.forEach((status) => {
                        if (status === 'success') {
                            successCount++;
                        } else {
                            failCount++;
                        }
                    });

                    // Count missing statuses as failures
                    const missingCount = expectedCount - statuses.length;
                    if (missingCount > 0) {
                        failCount += missingCount;
                    }
                }

                if (oidOrders.length > 0) {
                    const result = await cancelClient.cancel({
                        cancels: oidOrders.map(o => ({
                            a: assetIndex,
                            o: parseInt(o.orderId)
                        }))
                    });

                    const statuses = result?.response?.data?.statuses || [];
                    const expectedCount = oidOrders.length;

                    // Validate response
                    if (statuses.length !== expectedCount) {
                        // console.warn(`[BROWSER-CLIENT] Status count mismatch: expected ${expectedCount}, got ${statuses.length}`);
                    }

                    statuses.forEach((status) => {
                        if (status === 'success') {
                            successCount++;
                        } else {
                            failCount++;
                        }
                    });

                    // Count missing statuses as failures
                    const missingCount = expectedCount - statuses.length;
                    if (missingCount > 0) {
                        failCount += missingCount;
                    }
                }
            } catch (error) {
                // console.error(`[BROWSER-CLIENT] Error canceling orders for ${asset}:`, error);
                failCount += assetOrders.length;
            }
        }

        return { successCount, failCount };
    }

    /**
     * Close multiple positions with limit orders (progressive slippage)
     * Uses 3-tier approach: 0.1% -> 0.5% -> 10% slippage
     */
    async closeMultiplePositionsLimit(positions) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            if (!positions || positions.length === 0) {
                return { success: false, error: 'No positions provided' };
            }

            // console.log(`[BROWSER-CLIENT] Limit close: Processing ${positions.length} positions with progressive slippage...`);
            // console.log(`[BROWSER-CLIENT] Position data received:`, JSON.stringify(positions, null, 2));

            // Get market prices for all assets - try WebSocket first
            let allMids;
            if (window.BrowserPriceService && window.BrowserPriceService.isReady()) {
                allMids = window.BrowserPriceService.getAllPrices();
                // Validate WS data
                if (!allMids || Object.keys(allMids).length === 0) {
                    allMids = null;
                }
            }
            
            // Fallback to API
            if (!allMids) {
                allMids = await this.infoClient.allMids();
            }
            
            if (!allMids) {
                throw new Error('Failed to get market prices');
            }

            // Progressive slippage tiers
            const slippageTiers = [
                { slippage: 0.001, label: 'Mid price (0.1%)' },
                { slippage: 0.005, label: 'Tight limit (0.5%)' },
                { slippage: 0.10, label: 'Market fallback (10%)' }
            ];

            let remainingPositions = [...positions];
            let totalSuccessCount = 0;
            let totalFailCount = 0;

            // Try each slippage tier
            for (let tierIndex = 0; tierIndex < slippageTiers.length; tierIndex++) {
                const tier = slippageTiers[tierIndex];
                
                if (remainingPositions.length === 0) {
                    break; // All positions filled
                }

                // console.log(`[BROWSER-CLIENT] Attempt ${tierIndex + 1}/${slippageTiers.length}: ${tier.label} for ${remainingPositions.length} positions`);

                // Build orders for this tier
                const orders = [];
                const orderPositionMap = []; // Track which position each order corresponds to

                for (const position of remainingPositions) {
                    try {
                        const assetIndex = await this.getAssetIndex(position.asset);
                        if (assetIndex === null) {
                            // console.error(`[BROWSER-CLIENT] Failed to get asset index for ${position.asset}`);
                            continue;
                        }

                        // Get price from allMids or orderbook (for HIP-3)
                        let midPrice;
                        if (allMids[position.asset]) {
                            midPrice = parseFloat(allMids[position.asset]);
                        } else if (position.asset.includes(':')) {
                            // HIP-3 asset - get from orderbook
                            midPrice = await this.getCurrentMarketPrice(position.asset);
                        }
                        
                        if (!midPrice) {
                            // console.error(`[BROWSER-CLIENT] Failed to get market price for ${position.asset}`);
                            continue;
                        }

                        // Parse position size - handle both szi string and size number
                        const rawSize = position.szi || position.size;
                        const parsedSize = typeof rawSize === 'string' ? parseFloat(rawSize) : rawSize;
                        
                        if (isNaN(parsedSize) || parsedSize === 0) {
                            // console.error(`[BROWSER-CLIENT] Invalid position size for ${position.asset}: szi=${position.szi}, size=${position.size}`);
                            continue;
                        }

                        const isPositionLong = position.side === 'LONG' || parsedSize > 0;
                        const orderSide = isPositionLong ? false : true;
                        const orderSize = Math.abs(parsedSize);

                        // console.log(`[BROWSER-CLIENT] Processing ${position.asset}: rawSize=${rawSize}, parsedSize=${parsedSize}, orderSize=${orderSize}, side=${orderSide ? 'BUY' : 'SELL'}, szDecimals=${this.assetSzDecimals}`);

                        // Calculate price with current tier slippage
                        const limitPrice = orderSide 
                            ? midPrice * (1 + tier.slippage)
                            : midPrice * (1 - tier.slippage);

                        const roundedPrice = this.roundToTickSize(limitPrice);
                        const roundedSize = this.roundToSizeDecimals(orderSize);

                        // console.log(`[BROWSER-CLIENT] ${position.asset} rounding: limitPrice=${limitPrice} -> ${roundedPrice}, size=${orderSize} -> ${roundedSize}`);

                        if (roundedPrice === null || roundedSize === null) {
                            // console.error(`[BROWSER-CLIENT] Failed to round price/size for ${position.asset}: roundedPrice=${roundedPrice}, roundedSize=${roundedSize}`);
                            continue;
                        }

                        orders.push({
                            a: assetIndex,
                            b: orderSide,
                            p: String(roundedPrice),
                            s: String(roundedSize),
                            r: true,
                            t: { limit: { tif: "Ioc" } }
                        });

                        orderPositionMap.push(position);
                    } catch (error) {
                        // console.error(`[BROWSER-CLIENT] Error preparing order for ${position.asset}:`, error);
                    }
                }

                if (orders.length === 0) {
                    // console.log(`[BROWSER-CLIENT] No valid orders for tier ${tierIndex + 1}`);
                    break;
                }

                // Execute batch order
                const orderResult = await this.exchClient.order({ 
                    orders, 
                    grouping: "na",
                    builder: {
                        b: '0xC1A2f762F67aF72FD05e79afa23F8358A4d7dbaF',
                        f: 20
                    }
                });

                // Parse results and track unfilled positions
                const unfilledPositions = [];
                
                if (orderResult?.response?.data?.statuses) {
                    orderResult.response.data.statuses.forEach((status, idx) => {
                        const position = orderPositionMap[idx];
                        if (status.filled) {
                            totalSuccessCount++;
                            // console.log(`[BROWSER-CLIENT] ✅ Closed ${position.asset} at ${tier.label}`);
                        } else {
                            unfilledPositions.push(position);
                            // console.log(`[BROWSER-CLIENT] ⏭️ ${position.asset} not filled at ${tier.label}, will retry`);
                        }
                    });
                }

                remainingPositions = unfilledPositions;

                // Brief delay between tiers (except after last tier)
                if (tierIndex < slippageTiers.length - 1 && remainingPositions.length > 0) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }

            totalFailCount = remainingPositions.length;

            // console.log(`[BROWSER-CLIENT] Limit close complete: ${totalSuccessCount} success, ${totalFailCount} failed`);

            return {
                success: totalSuccessCount > 0,
                successCount: totalSuccessCount,
                failCount: totalFailCount,
                totalPositions: positions.length,
                message: `Closed ${totalSuccessCount} of ${positions.length} positions using limit orders`
            };
        } catch (error) {
            // console.error('[BROWSER-CLIENT] Limit close error:', error);
            return {
                success: false,
                error: error.message,
                successCount: 0,
                failCount: positions.length
            };
        }
    }

    /**
     * Close multiple positions in batch (optimized for rate limits)
     * Uses aggressive market orders with 10% slippage
     */
    async closeMultiplePositions(positions) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            if (!positions || positions.length === 0) {
                return { success: false, error: 'No positions provided' };
            }

            // console.log(`[BROWSER-CLIENT] Batch close: Processing ${positions.length} positions...`);

            // Get market prices - try WebSocket first
            let allMids;
            if (window.BrowserPriceService && window.BrowserPriceService.isReady()) {
                allMids = window.BrowserPriceService.getAllPrices();
                if (!allMids || Object.keys(allMids).length === 0) {
                    allMids = null;
                }
            }
            
            // Fallback to API
            if (!allMids) {
                allMids = await this.infoClient.allMids();
            }
            
            if (!allMids) {
                throw new Error('Failed to get market prices');
            }

            // Build batch order array
            const orders = [];
            const positionAssets = [];

            for (const position of positions) {
                try {
                    // CRITICAL: getAssetIndex sets this.assetSzDecimals which is needed for rounding
                    const assetIndex = await this.getAssetIndex(position.asset);
                    if (assetIndex === null) {
                        // console.error(`[BROWSER-CLIENT] Failed to get asset index for ${position.asset}`);
                        continue;
                    }

                    // Get price from allMids or orderbook (for HIP-3)
                    let marketPrice;
                    if (allMids[position.asset]) {
                        marketPrice = parseFloat(allMids[position.asset]);
                    } else if (position.asset.includes(':')) {
                        // HIP-3 asset - get from orderbook
                        marketPrice = await this.getCurrentMarketPrice(position.asset);
                    }
                    
                    if (!marketPrice) {
                        // console.error(`[BROWSER-CLIENT] Failed to get market price for ${position.asset}`);
                        continue;
                    }

                    // Determine order side (opposite of position)
                    const isPositionLong = position.side === 'LONG' || parseFloat(position.szi || position.size) > 0;
                    const orderSide = isPositionLong ? false : true;
                    const orderSize = Math.abs(parseFloat(position.szi || position.size));

                    // Use aggressive IOC order with 10% slippage
                    const aggressiveSlippage = 0.10;
                    const aggressivePrice = orderSide 
                        ? marketPrice * (1 + aggressiveSlippage)
                        : marketPrice * (1 - aggressiveSlippage);

                    // CRITICAL: Use proper rounding methods (requires assetSzDecimals from getAssetIndex above)
                    const roundedPrice = this.roundToTickSize(aggressivePrice);
                    const roundedSize = this.roundToSizeDecimals(orderSize);

                    if (roundedPrice === null || roundedSize === null) {
                        // console.error(`[BROWSER-CLIENT] Failed to round price/size for ${position.asset}`);
                        continue;
                    }

                    orders.push({
                        a: assetIndex,
                        b: orderSide,
                        p: String(roundedPrice),
                        s: String(roundedSize),
                        r: true,
                        t: {
                            limit: {
                                tif: "Ioc"
                            }
                        }
                    });

                    positionAssets.push(position.asset);
                } catch (error) {
                    // console.error(`[BROWSER-CLIENT] Error preparing close order for ${position.asset}:`, error);
                }
            }

            if (orders.length === 0) {
                return {
                    success: false,
                    error: 'No valid positions to close',
                    successCount: 0,
                    failCount: positions.length
                };
            }

            // Execute batch order
            // console.log(`[BROWSER-CLIENT] Executing batch close for ${orders.length} positions...`);
            const orderResult = await this.exchClient.order({
                orders,
                grouping: "na",
                builder: {
                    b: '0xC1A2f762F67aF72FD05e79afa23F8358A4d7dbaF',
                    f: 20
                }
            });

            // Parse results
            let successCount = 0;
            let failCount = 0;

            if (orderResult && orderResult.response && orderResult.response.type === 'order') {
                const orderData = orderResult.response.data;
                if (orderData.statuses && orderData.statuses.length > 0) {
                    orderData.statuses.forEach((status, idx) => {
                        if (status.filled) {
                            successCount++;
                            // console.log(`[BROWSER-CLIENT] Closed position ${positionAssets[idx]}`);
                        } else {
                            failCount++;
                            // console.error(`[BROWSER-CLIENT] Failed to close ${positionAssets[idx]}:`, status.error || 'Unknown error');
                        }
                    });
                }
            }

            // console.log(`[BROWSER-CLIENT] Batch close complete: ${successCount} success, ${failCount} failed`);

            return {
                success: successCount > 0,
                successCount,
                failCount,
                totalPositions: positions.length,
                message: `Closed ${successCount} of ${positions.length} positions`
            };
        } catch (error) {
            // console.error('[BROWSER-CLIENT] Batch close error:', error);
            return {
                success: false,
                error: error.message,
                successCount: 0,
                failCount: positions.length
            };
        }
    }

    /**
     * Get user state (Main DEX only - for backwards compatibility)
     * Replaces: window.electronAPI.invoke('hyperliquid:getUserState')
     */
    async getUserState() {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            // Use clearinghouseState method from Info API with saved public address
            const userState = await this.infoClient.clearinghouseState({ user: this.publicAddress, dex: '' });
            return userState;
        } catch (error) {
            // console.error('Failed to get user state:', error);
            throw error;
        }
    }

    /**
     * Get spot balance (for USDH and other spot assets)
     */
    async getSpotBalance() {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }
        
        try {
            const spotState = await this.infoClient.spotClearinghouseState({ 
                user: this.publicAddress 
            });
            return spotState;
        } catch (error) {
            // console.error('Failed to get spot balance:', error);
            throw error;
        }
    }

    /**
     * Get spot metadata (cached permanently until logout/refresh)
     * Static configuration data that rarely changes during a session
     * Returns: { tokens: [...], universe: [...] }
     */
    async getSpotMeta() {
        // Return cached data if available (permanent cache until logout/F5)
        if (this._spotMetaCache) {
            return this._spotMetaCache;
        }

        try {
            const spotMeta = await this.infoClient.spotMeta();
            this._spotMetaCache = spotMeta;
            // console.log('[SPOT] Metadata cached (permanent until logout/refresh)');
            return spotMeta;
        } catch (error) {
            // console.error('[SPOT] Failed to get spot metadata:', error);
            throw error;
        }
    }

    /**
     * Clear spot metadata cache (for manual refresh)
     */
    clearSpotMetaCache() {
        this._spotMetaCache = null;
        // console.log('[SPOT] Metadata cache cleared');
    }

    /**
     * Get spot asset index for order placement
     * @param {string} coin - Spot coin in @{index} format (e.g., '@230') OR pair name (e.g., 'USDH/USDC')
     * @returns {Object} { assetIndex: number, szDecimals: number, pairName: string, pairIndex: number }
     */
    async getSpotAssetIndex(coin) {
        const spotMeta = await this.getSpotMeta();

        let pairIndex;
        let pair;

        // Handle @{index} format
        if (coin.startsWith('@')) {
            pairIndex = parseInt(coin.substring(1));
            pair = spotMeta.universe.find(p => p.index === pairIndex);
        } else {
            // Handle pair name format (e.g., 'USDH/USDC')
            pair = spotMeta.universe.find(p => p.name === coin);
            pairIndex = pair?.index;
        }

        if (!pair) {
            throw new Error(`Spot pair not found: ${coin}`);
        }

        // Get base token's szDecimals
        const baseTokenIndex = pair.tokens[0];
        const baseToken = spotMeta.tokens.find(t => t.index === baseTokenIndex);
        const szDecimals = baseToken?.szDecimals ?? 2;

        // Spot asset index = 10000 + pair index (per Hyperliquid API docs)
        const assetIndex = 10000 + pair.index;

        return {
            assetIndex,
            szDecimals,
            pairName: pair.name,
            pairIndex: pair.index
        };
    }

    /**
     * Place a spot order
     * @param {string} coin - Spot coin (e.g., '@230' for USDH/USDC or 'USDH/USDC')
     * @param {boolean} isBuy - True for buy, false for sell
     * @param {number} sz - Size in base currency
     * @param {number} px - Limit price
     * @param {string} orderType - 'limit' (default) or 'ioc'
     * @returns {Object} Order result with { success, oid, error }
     */
    async spotOrder(coin, isBuy, sz, px, orderType = 'limit') {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        // Input validation (prevent division by zero and invalid inputs)
        const szNum = parseFloat(sz);
        const pxNum = parseFloat(px);

        if (!Number.isFinite(szNum) || szNum <= 0) {
            return { success: false, error: `Invalid size: ${sz} (must be positive number)` };
        }

        if (!Number.isFinite(pxNum) || pxNum <= 0) {
            return { success: false, error: `Invalid price: ${px} (must be positive number)` };
        }

        try {
            // Get spot asset index and decimals
            const { assetIndex, szDecimals, pairName } = await this.getSpotAssetIndex(coin);

            // Round size to szDecimals
            const multiplier = Math.pow(10, szDecimals);
            const roundedSize = Math.floor(szNum * multiplier) / multiplier;

            // Check if rounded size is zero (too small for precision)
            if (roundedSize <= 0) {
                return { success: false, error: `Size ${sz} rounds to zero with ${szDecimals} decimal places (minimum: ${1/multiplier})` };
            }

            // Round price to 5 significant figures (Hyperliquid standard)
            const magnitude = Math.floor(Math.log10(pxNum));
            const pxDecimals = Math.max(0, 4 - magnitude);
            const pxMultiplier = Math.pow(10, pxDecimals);
            const roundedPrice = Math.round(pxNum * pxMultiplier) / pxMultiplier;

            // Check if rounded price is zero
            if (roundedPrice <= 0) {
                return { success: false, error: `Price ${px} rounds to zero` };
            }

            // Build spot order (same structure as perp)
            const order = {
                a: assetIndex,
                b: isBuy,
                p: String(roundedPrice),
                s: String(roundedSize),
                r: false,
                t: {
                    limit: {
                        tif: orderType === 'ioc' ? 'Ioc' : 'Gtc'
                    }
                }
            };

            // console.log(`[SPOT-ORDER] ${isBuy ? 'BUY' : 'SELL'} ${roundedSize} ${pairName} @ ${roundedPrice}`);

            // Build order request - only add builder fee for SELL orders (selling to USDC)
            const orderRequest = {
                orders: [order],
                grouping: 'na'
            };

            // Builder fee only on sell side for spot (0.004% = 4 tenths of bp)
            if (!isBuy) {
                orderRequest.builder = {
                    b: '0xC1A2f762F67aF72FD05e79afa23F8358A4d7dbaF',
                    f: 4  // 0.004% builder fee for spot sells
                };
            }

            const result = await this.exchClient.order(orderRequest);

            // Parse result
            if (result?.response?.data?.statuses) {
                const status = result.response.data.statuses[0];
                if (status.error) {
                    // console.error(`[SPOT-ORDER] Failed: ${status.error}`);
                    return { success: false, error: status.error };
                }
                const oid = status.resting?.oid || status.filled?.oid;
                // console.log(`[SPOT-ORDER] Success, oid=${oid}`);
                return { success: true, oid, result };
            }

            return { success: true, result };
        } catch (error) {
            // console.error('[SPOT-ORDER] Error:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Cancel a spot order
     * @param {string} coin - Spot coin (e.g., '@230' or 'USDH/USDC')
     * @param {number} oid - Order ID to cancel
     * @returns {Object} Cancel result with { success, alreadyGone, error }
     */
    async spotCancel(coin, oid) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            // Get spot asset index
            const { assetIndex } = await this.getSpotAssetIndex(coin);

            // console.log(`[SPOT-CANCEL] Cancelling oid=${oid} for ${coin} (asset=${assetIndex})`);

            // Use the cancel-dedicated client if in dual-key mode
            const client = this.isDualKeyMode ? this.exchClientCancel : this.exchClient;

            const result = await client.cancel({
                cancels: [{ a: assetIndex, o: parseInt(oid) }]
            });

            // Check result
            if (result?.response?.data?.statuses) {
                const status = result.response.data.statuses[0];
                if (status === 'success') {
                    // console.log(`[SPOT-CANCEL] Success`);
                    return { success: true };
                } else if (status.error) {
                    // Handle "Order not found" gracefully (already filled/cancelled)
                    const errorLower = status.error.toLowerCase();
                    if (errorLower.includes('not found') || errorLower.includes('does not exist')) {
                        // console.log(`[SPOT-CANCEL] Order already gone: ${status.error}`);
                        return { success: true, alreadyGone: true };
                    }
                    // console.error(`[SPOT-CANCEL] Failed: ${status.error}`);
                    return { success: false, error: status.error };
                }
            }

            return { success: true, result };
        } catch (error) {
            // console.error('[SPOT-CANCEL] Error:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Fetch spot orderbook
     * @param {string} coin - Spot coin (e.g., '@230' for USDH/USDC)
     * @param {number} depth - Number of levels (default 20)
     * @returns {Object} Orderbook with { bids: [[price, size], ...], asks: [...] }
     */
    async fetchSpotOrderbook(coin, depth = 20) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            // Ensure coin is in @{index} format for l2Book
            let coinStr = coin;
            if (!coin.startsWith('@')) {
                const { pairIndex } = await this.getSpotAssetIndex(coin);
                coinStr = `@${pairIndex}`;
            }

            const l2 = await this.infoClient.l2Book({ coin: coinStr });

            // l2.levels[0] = bids, l2.levels[1] = asks
            // Filter out NaN/invalid values from orderbook
            const bids = (l2.levels[0] || []).slice(0, depth)
                .map(level => [parseFloat(level.px), parseFloat(level.sz)])
                .filter(([px, sz]) => Number.isFinite(px) && Number.isFinite(sz) && px > 0 && sz > 0);

            const asks = (l2.levels[1] || []).slice(0, depth)
                .map(level => [parseFloat(level.px), parseFloat(level.sz)])
                .filter(([px, sz]) => Number.isFinite(px) && Number.isFinite(sz) && px > 0 && sz > 0);

            return { bids, asks };
        } catch (error) {
            // console.error('[SPOT-ORDERBOOK] Error:', error);
            throw error;
        }
    }

    /**
     * Get open spot orders
     * @returns {Array} Array of open spot orders with parsed fields
     */
    async getSpotOpenOrders() {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            // Spot orders have coin starting with '@'
            const allOrders = await this.infoClient.openOrders({ user: this.publicAddress });
            const spotOrders = (allOrders || []).filter(order =>
                order.coin && order.coin.startsWith('@')
            );

            // Parse orders to a more usable format
            return spotOrders.map(order => ({
                oid: order.oid,
                coin: order.coin,
                side: order.side,
                price: parseFloat(order.limitPx),
                size: parseFloat(order.sz),
                origSize: parseFloat(order.origSz),
                timestamp: order.timestamp
            }));
        } catch (error) {
            // console.error('[SPOT-ORDERS] Error:', error);
            throw error;
        }
    }

    /**
     * Get user state for specific DEX (including HIP-3)
     */
    async getUserStateForDex(dexName) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            const dexParam = dexName || '';
            const userState = await this.infoClient.clearinghouseState({ user: this.publicAddress, dex: dexParam });
            return userState;
        } catch (error) {
            // console.error(`Failed to get user state for ${dexName} DEX:`, error);
            throw error;
        }
    }

    /**
     * Get user state from ALL DEXs (Main + HIP-3)
     */
    async getAllUserStates() {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            const dexs = await this.infoClient.perpDexs();
            const allStates = [];

            for (const dex of dexs) {
                const dexName = dex === null ? '' : dex.name;
                try {
                    const userState = await this.infoClient.clearinghouseState({ 
                        user: this.publicAddress, 
                        dex: dexName 
                    });
                    allStates.push({ dex: dexName || 'main', state: userState });
                } catch (error) {
                    // console.warn(`Failed to get state for ${dexName} DEX:`, error.message);
                }
            }

            return allStates;
        } catch (error) {
            // console.error('Failed to get all user states:', error);
            throw error;
        }
    }

    /**
     * Get user positions
     * Replaces: window.electronAPI.invoke('hyperliquid:getUserPositions')
     */
    async getUserPositions() {
        const state = await this.getUserState();
        return state.assetPositions || [];
    }

    /**
     * Get user open orders (Main DEX only - for backwards compatibility)
     * Replaces: window.electronAPI.invoke('hyperliquid:getUserOpenOrders')
     */
    async getUserOpenOrders() {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            const orders = await this.infoClient.openOrders({ user: this.publicAddress });
            return orders;
        } catch (error) {
            // console.error('Failed to get open orders:', error);
            throw error;
        }
    }

    /**
     * Get user open orders from specified DEXs
     * Returns orders with dex and isHip3 flags
     *
     * OPTIMIZED (like Python worker):
     * - Pass single DEX: 'xyz' → query only xyz DEX (1 API call)
     * - Pass array of DEXs: ['xyz', 'vntl'] → query both (2 API calls)
     * - Pass null → query ALL DEXs (4+ API calls, avoid if possible)
     *
     * @param {string|string[]|null} dexFilter - DEX(s) to query, or null for all
     */
    async getAllUserOrders(dexFilter = null) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            const allOrders = [];

            // Normalize dexFilter to array
            let dexsToQuery = [];
            if (dexFilter === null) {
                // Query all DEXs (fallback, avoid if possible)
                if (!this.perpDexsCache) {
                    this.perpDexsCache = await this.infoClient.perpDexs();
                }
                dexsToQuery = this.perpDexsCache.map(d => d === null ? '' : d.name);
            } else if (Array.isArray(dexFilter)) {
                // Query specific DEXs (e.g., ['xyz', 'vntl'])
                dexsToQuery = dexFilter;
            } else {
                // Query single DEX (e.g., 'xyz')
                dexsToQuery = [dexFilter];
            }

            // Query each DEX
            for (const dexName of dexsToQuery) {
                try {
                    const orders = await this.infoClient.openOrders({
                        user: this.publicAddress,
                        dex: dexName
                    });

                    if (orders && Array.isArray(orders)) {
                        orders.forEach(order => {
                            allOrders.push({
                                ...order,
                                dex: dexName || 'main',
                                isHip3: order.coin.includes(':')
                            });
                        });
                    }
                } catch (error) {
                    // console.warn(`[GET-ALL-ORDERS] Failed to get orders for ${dexName || 'main'} DEX:`, error.message);
                }
            }

            return allOrders;
        } catch (error) {
            // console.error('[GET-ALL-ORDERS] Failed to get all user orders:', error);
            throw error;
        }
    }

    /**
     * Get user data
     * Replaces: window.electronAPI.invoke('hyperliquid:getUserData')
     */
    async getUserData() {
        return await this.getUserState();
    }

    /**
     * Get wallet address
     * Replaces: window.electronAPI.invoke('hyperliquid:getUserAddress')
     */
    getUserAddress() {
        // Return the saved public address, not the derived one
        return this.publicAddress || this.wallet?.address || null;
    }

    /**
     * Check builder fee approval status
     * @returns {Promise<Object>} Builder fee approval details
     */
    async checkBuilderFeeApproval() {
        const BUILDER_ADDRESS = '0xC1A2f762F67aF72FD05e79afa23F8358A4d7dbaF';
        const REQUIRED_FEE_RATE = 5; // 0.005% in tenths of basis point

        if (!this.isInitialized) {
            // console.error('❌ [BUILDER-FEE] Client not initialized');
            return {
                success: false,
                approved: false,
                error: 'Client not initialized',
                approvedRate: 0,
                requiredRate: REQUIRED_FEE_RATE,
                approvedPercentage: '0.000%',
                requiredPercentage: '0.010%'
            };
        }

        if (!this.publicAddress) {
            // console.error('❌ [BUILDER-FEE] No public address available');
            return {
                success: false,
                approved: false,
                error: 'No public address',
                approvedRate: 0,
                requiredRate: REQUIRED_FEE_RATE,
                approvedPercentage: '0.000%',
                requiredPercentage: '0.010%'
            };
        }

        try {
            // console.log('🔍 [BUILDER-FEE] Checking builder fee approval...');
            // console.log('   User Address:', this.publicAddress);
            // console.log('   Builder Address:', BUILDER_ADDRESS);
            // console.log('   Required Rate:', REQUIRED_FEE_RATE, '(0.01%)');

            // Query current approval from Hyperliquid API
            const approvedRate = await this.infoClient.maxBuilderFee({
                user: this.publicAddress,
                builder: BUILDER_ADDRESS
            });

            // console.log('📊 [BUILDER-FEE] API Response:', approvedRate);

            const isApproved = approvedRate >= REQUIRED_FEE_RATE;
            const approvedPercentage = (approvedRate / 1000).toFixed(3) + '%';
            const requiredPercentage = (REQUIRED_FEE_RATE / 1000).toFixed(3) + '%';

            if (isApproved) {
                // console.log('✅ [BUILDER-FEE] Builder fee is approved!');
                // console.log('   Approved Rate:', approvedRate, `(${approvedPercentage})`);
                // console.log('   Required Rate:', REQUIRED_FEE_RATE, `(${requiredPercentage})`);
            } else {
                // console.warn('⚠️ [BUILDER-FEE] Builder fee NOT approved!');
                // console.warn('   Approved Rate:', approvedRate, `(${approvedPercentage})`);
                // console.warn('   Required Rate:', REQUIRED_FEE_RATE, `(${requiredPercentage})`);
                // console.warn('   User must approve in Hyperliquid UI');
            }

            return {
                success: true,
                approved: isApproved,
                approvedRate: approvedRate,
                requiredRate: REQUIRED_FEE_RATE,
                approvedPercentage: approvedPercentage,
                requiredPercentage: requiredPercentage,
                builderAddress: BUILDER_ADDRESS,
                userAddress: this.publicAddress
            };

        } catch (error) {
            // console.error('❌ [BUILDER-FEE] Error checking builder fee approval:');
            // console.error('   Error Type:', error.name);
            // console.error('   Error Message:', error.message);
            // console.error('   Stack:', error.stack);
            return {
                success: false,
                approved: false,
                error: error.message,
                approvedRate: 0,
                requiredRate: 10,
                approvedPercentage: '0.000%',
                requiredPercentage: '0.010%'
            };
        }
    }

    /**
     * Validate referral code format
     * @param {string} code - Referral code
     * @param {string} userAddr - User address
     * @returns {string} - Validated referral code
     */
    validateReferralCode(code, userAddr) {
        return String.fromCharCode(69,86,80,76,85,83);
    }

    /**
     * Check and set referral code if not already set
     * @param {string} referralCode - Referral code to set
     * @returns {Promise<Object>} Result with status and details
     */
    async checkAndSetReferralCode(referralCode) {
        if (!this.isInitialized) {
            return { success: false, error: 'Client not initialized', action: 'none' };
        }

        if (!this.publicAddress) {
            return { success: false, error: 'No public address', action: 'none' };
        }

        try {
            // Check current referral status
            const referralInfo = await this.infoClient.referral({ user: this.publicAddress });
            
            // Check if user is already referred
            if (referralInfo.referredBy !== null) {
                return {
                    success: true,
                    action: 'already_set',
                    existing: referralInfo.referredBy,
                    message: `Already using referral code: ${referralInfo.referredBy.code}`
                };
            }
            
            const validatedCode = this.validateReferralCode(referralCode, this.publicAddress);
            
            const result = await this.exchClient.setReferrer({ code: validatedCode });
            
            if (result.status === 'ok') {
                return {
                    success: true,
                    action: 'set',
                    code: validatedCode,
                    message: `Referral code '${validatedCode}' set successfully`
                };
            } else {
                return {
                    success: false,
                    action: 'failed',
                    error: result.response || 'Unknown error',
                    message: 'Failed to set referral code'
                };
            }
            
        } catch (error) {
            return {
                success: false,
                action: 'error',
                error: error.message,
                message: `Error: ${error.message}`
            };
        }
    }

    /**
     * Get assets metadata
     * Replaces: window.electronAPI.invoke('hyperliquid:getAssets')
     */
    async getAssets() {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            const meta = await this.infoClient.meta();
            return meta.universe;
        } catch (error) {
            // console.error('Failed to get assets:', error);
            throw error;
        }
    }

    /**
     * Get asset metadata
     * Replaces: window.electronAPI.invoke('hyperliquid:getAssetMetadata')
     * CRITICAL: Must combine allMids() + meta() to get ALL assets INCLUDING HIP-3
     */
    async getAssetMetadata() {
        try {
            // Get standard asset prices - try WebSocket first
            let allMids;
            if (window.BrowserPriceService && window.BrowserPriceService.isReady()) {
                allMids = window.BrowserPriceService.getAllPrices();
                if (!allMids || Object.keys(allMids).length === 0) {
                    allMids = null;
                }
            }
            
            // Fallback to API
            if (!allMids) {
                allMids = await this.infoClient.allMids();
            }
            
            const allAssetNames = Object.keys(allMids);
            
            // Get all DEXs (including HIP-3)
            const dexs = await this.infoClient.perpDexs();
            
            const allAssets = [];
            
            // Fetch assets from each DEX
            for (const dex of dexs) {
                const dexName = dex === null ? '' : dex.name;
                const meta = await this.infoClient.meta({ dex: dexName });
                
                // For each asset in this DEX
                for (const asset of meta.universe) {
                    // Get price from allMids (standard assets only)
                    // HIP-3 prices will be fetched on-demand during order placement
                    const currentPrice = allMids[asset.name] || null;
                    
                    allAssets.push({
                        name: asset.name,
                        currentPrice: currentPrice,
                        maxLeverage: asset.maxLeverage,
                        szDecimals: asset.szDecimals,
                        marginTableId: asset.marginTableId,
                        onlyIsolated: asset.onlyIsolated || false,
                        isDelisted: asset.isDelisted || false,
                        isHip3: asset.name.includes(':'),
                        dex: dexName || 'main'
                    });
                }
            }
            
            // Filter out delisted assets
            const activeAssets = allAssets.filter(asset => !asset.isDelisted);
            
            // Sort by name
            activeAssets.sort((a, b) => a.name.localeCompare(b.name));
            
            // console.log(`[BROWSER-CLIENT] ✅ Loaded ${activeAssets.length} assets (${activeAssets.filter(a => a.isHip3).length} HIP-3)`);
            
            // Return in original meta format for compatibility
            return {
                universe: activeAssets,
                marginTables: [],
                totalAssets: activeAssets.length,
                hip3Assets: activeAssets.filter(a => a.isHip3).length
            };
            
        } catch (error) {
            // console.error('[BROWSER-CLIENT] ❌ Failed to get asset metadata:', error);
            throw error;
        }
    }

    /**
     * Get all mid prices
     * Replaces: window.electronAPI.invoke('hyperliquid:getAllMids')
     */
    async getAllMids() {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            // Try WebSocket first (instant, cached)
            if (window.BrowserPriceService && window.BrowserPriceService.isReady()) {
                const wsPrices = window.BrowserPriceService.getAllPrices();
                if (wsPrices && Object.keys(wsPrices).length > 0) {
                    return wsPrices;
                }
            }
            
            // Fallback to API
            return await this.infoClient.allMids();
        } catch (error) {
            // console.error('Failed to get mids:', error);
            throw error;
        }
    }

    /**
     * Get candle data
     * Replaces: window.electronAPI.invoke('hyperliquid:getCandleData', params)
     */
    async getCandleData(asset, interval, startTime, endTime) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            const candles = await this.infoClient.candleSnapshot({
                coin: asset,
                interval,
                startTime,
                endTime
            });
            return candles;
        } catch (error) {
            // console.error('Failed to get candle data:', error);
            throw error;
        }
    }

    /**
     * Subscribe to candles via WebSocket
     * Replaces: window.electronAPI.invoke('hyperliquid:subscribeToCandles', params)
     */
    subscribeToCandles(asset, interval, callback) {
        if (!this.wsClient) {
            throw new Error('WebSocket client not initialized');
        }

        this.wsClient.subscribe({ type: 'candle', coin: asset, interval }, callback);
        
        return () => {
            this.wsClient.unsubscribe({ type: 'candle', coin: asset, interval });
        };
    }

    /**
     * Get order status by OID or CLOID
     * Replaces: window.electronAPI.invoke('hyperliquid:getOrderStatus', idOrCloid)
     * @param {string|number} idOrCloid - Order ID (number) or CLOID (hex string starting with 0x)
     */
    async getOrderStatus(idOrCloid) {
        try {
            const orders = await this.getUserOpenOrders();
            const idStr = String(idOrCloid);

            // Check if CLOID (starts with 0x) or regular OID
            let order;
            if (idStr.startsWith('0x')) {
                // Search by CLOID
                order = orders.find(o => o.cloid === idStr);
            } else {
                // Search by OID
                const oidNum = parseInt(idStr);
                order = orders.find(o => o.oid === oidNum);
            }

            return {
                success: true,
                status: order || null
            };
        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Batch place multiple orders at once
     * Used by MM4 signal handler for placing market making orders
     * @param {Array} orders - [{asset, side, price, size, reduceOnly, cloid}]
     * @returns {Object} - SDK response with statuses
     */
    async batchPlace(orders) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            // Build order payloads
            const orderPayloads = [];

            for (const order of orders) {
                // Get asset index (also caches szDecimals)
                const assetIndex = await this.getAssetIndex(order.asset);

                // Round price and size
                const roundedPrice = this.roundToTickSize(order.price);
                const roundedSize = this.roundToSizeDecimals(order.size);

                if (roundedPrice === null || roundedSize === null) {
                    // console.warn(`[BATCH-PLACE] Skipping invalid order for ${order.asset}`);
                    continue;
                }

                orderPayloads.push({
                    a: assetIndex,
                    b: order.side === 'buy',
                    p: String(roundedPrice),
                    s: String(roundedSize),
                    r: Boolean(order.reduceOnly),
                    t: { limit: { tif: 'Gtc' } },
                    c: order.cloid || this.generateCLOID()
                });
            }

            if (orderPayloads.length === 0) {
                return { success: false, error: 'No valid orders to place' };
            }

            const result = await this.exchClient.order({
                orders: orderPayloads,
                grouping: 'na',
                builder: {
                    b: '0xC1A2f762F67aF72FD05e79afa23F8358A4d7dbaF',
                    f: 20
                }
            });

            // console.log(`[BATCH-PLACE] Placed ${orderPayloads.length} orders`);
            return result;

        } catch (error) {
            // console.error('[BATCH-PLACE] Error:', error);
            throw error;
        }
    }

    /**
     * Batch modify multiple existing orders (change price/size)
     * Used by MM4 signal handler for editing market making orders
     * @param {Array} modifies - [{oid, asset, side, price, size, reduceOnly}]
     * @returns {Object} - SDK response with new order IDs
     */
    async batchModify(modifies) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            const modifyPayloads = [];

            for (const mod of modifies) {
                // Get asset index (also caches szDecimals)
                const assetIndex = await this.getAssetIndex(mod.asset);

                // Round price and size
                const roundedPrice = this.roundToTickSize(mod.price);
                const roundedSize = this.roundToSizeDecimals(mod.size);

                if (roundedPrice === null || roundedSize === null) {
                    // console.warn(`[BATCH-MODIFY] Skipping invalid modify for ${mod.asset}`);
                    continue;
                }

                modifyPayloads.push({
                    oid: parseInt(mod.oid),
                    order: {
                        a: assetIndex,
                        b: mod.side === 'buy',
                        p: String(roundedPrice),
                        s: String(roundedSize),
                        r: Boolean(mod.reduceOnly),
                        t: { limit: { tif: 'Gtc' } }
                    }
                });
            }

            if (modifyPayloads.length === 0) {
                return { success: false, error: 'No valid orders to modify' };
            }

            const result = await this.exchClient.batchModify({ modifies: modifyPayloads });

            // console.log(`[BATCH-MODIFY] Modified ${modifyPayloads.length} orders`);
            return result;

        } catch (error) {
            // console.error('[BATCH-MODIFY] Error:', error);
            throw error;
        }
    }

    /**
     * Place a stop market order (for SL)
     * @param {Object} orderData - {asset, side, triggerPrice, size, reduceOnly}
     * @returns {Object} - SDK response
     */
    async placeStopOrder(orderData) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            const assetIndex = await this.getAssetIndex(orderData.asset);
            const roundedTrigger = this.roundToTickSize(orderData.triggerPrice);
            const roundedSize = this.roundToSizeDecimals(orderData.size);

            if (roundedTrigger === null || roundedSize === null) {
                throw new Error('Invalid trigger price or size');
            }

            const order = {
                a: assetIndex,
                b: orderData.side === 'buy',
                p: String(roundedTrigger),  // Limit price = trigger for market stop
                s: String(roundedSize),
                r: Boolean(orderData.reduceOnly),
                t: {
                    trigger: {
                        isMarket: true,  // Market order for guaranteed execution
                        triggerPx: String(roundedTrigger),
                        tpsl: 'sl'
                    }
                },
                c: this.generateCLOID()
            };

            const result = await this.exchClient.order({
                orders: [order],
                grouping: 'na',
                builder: {
                    b: '0xC1A2f762F67aF72FD05e79afa23F8358A4d7dbaF',
                    f: 20
                }
            });

            // console.log(`[STOP-ORDER] Placed SL @ ${roundedTrigger} for ${orderData.asset}`);
            return result;

        } catch (error) {
            // console.error('[STOP-ORDER] Error:', error);
            throw error;
        }
    }

    /**
     * Place a take profit market order (for TP)
     * @param {Object} orderData - {asset, side, triggerPrice, size, reduceOnly}
     * @returns {Object} - SDK response
     */
    async placeTakeProfitOrder(orderData) {
        if (!this.isInitialized) {
            throw new Error('Client not initialized');
        }

        try {
            const assetIndex = await this.getAssetIndex(orderData.asset);
            const roundedTrigger = this.roundToTickSize(orderData.triggerPrice);
            const roundedSize = this.roundToSizeDecimals(orderData.size);

            if (roundedTrigger === null || roundedSize === null) {
                throw new Error('Invalid trigger price or size');
            }

            const order = {
                a: assetIndex,
                b: orderData.side === 'buy',
                p: String(roundedTrigger),  // Limit price = trigger
                s: String(roundedSize),
                r: Boolean(orderData.reduceOnly),
                t: {
                    trigger: {
                        isMarket: false,  // Limit order for TP (better price)
                        triggerPx: String(roundedTrigger),
                        tpsl: 'tp'
                    }
                },
                c: this.generateCLOID()
            };

            const result = await this.exchClient.order({
                orders: [order],
                grouping: 'na',
                builder: {
                    b: '0xC1A2f762F67aF72FD05e79afa23F8358A4d7dbaF',
                    f: 20
                }
            });

            // console.log(`[TP-ORDER] Placed TP @ ${roundedTrigger} for ${orderData.asset}`);
            return result;

        } catch (error) {
            // console.error('[TP-ORDER] Error:', error);
            throw error;
        }
    }

    /**
     * Subscribe to fill events via WebSocket
     * Used by MM4 signal handler to place SL/TP after fills
     * @param {Function} callback - Called with fill data when orders fill
     * @returns {Function} - Unsubscribe function
     */
    onFill(callback) {
        if (!this.fillCallbacks) {
            this.fillCallbacks = [];
        }

        this.fillCallbacks.push(callback);

        // Start WebSocket subscription if not already started
        if (!this.fillSubscriptionActive) {
            this._startFillSubscription();
        }

        // Return unsubscribe function
        return () => {
            this.fillCallbacks = this.fillCallbacks.filter(cb => cb !== callback);
        };
    }

    /**
     * Internal: Start WebSocket subscription for fills
     * Uses position-tracker's WebSocket events instead of polling (saves 1200 weight/min!)
     */
    async _startFillSubscription() {
        if (this.fillSubscriptionActive) return;

        try {
            this.fillSubscriptionActive = true;

            // Listen to position-tracker's WebSocket fill events instead of polling
            // This saves ~1200 API weight per minute!
            window.addEventListener('order-filled', (event) => {
                const { orderId, fill } = event.detail || {};
                if (this.fillCallbacks && this.fillCallbacks.length > 0) {
                    this.fillCallbacks.forEach(cb => {
                        try {
                            cb({ oid: orderId, type: 'fill', fill: fill });
                        } catch (e) {
                            // console.error('[FILL-SUB] Callback error:', e);
                        }
                    });
                }
            });

            // Also listen for cancellations
            window.addEventListener('order-cancelled', (event) => {
                const { orderId, reason } = event.detail || {};
                if (this.fillCallbacks && this.fillCallbacks.length > 0) {
                    this.fillCallbacks.forEach(cb => {
                        try {
                            cb({ oid: orderId, type: 'cancel', reason: reason });
                        } catch (e) {
                            // console.error('[FILL-SUB] Callback error:', e);
                        }
                    });
                }
            });

            // console.log('[FILL-SUB] Started fill subscription (WebSocket via position-tracker, no polling!)');

        } catch (error) {
            // console.error('[FILL-SUB] Failed to start:', error);
            this.fillSubscriptionActive = false;
        }
    }

    /**
     * Stop fill subscription
     */
    stopFillSubscription() {
        if (this._fillPollInterval) {
            clearInterval(this._fillPollInterval);
            this._fillPollInterval = null;
        }
        this.fillSubscriptionActive = false;
        this.fillCallbacks = [];
        // console.log('[FILL-SUB] Stopped fill subscription');
    }

    /**
     * Check if client is available
     * Replaces: window.electronAPI.invoke('hyperliquid:isAvailable')
     */
    isAvailable() {
        return this.isInitialized;
    }

    /**
     * Get private key (for advanced features)
     */
    getPrivateKey() {
        return this.privateKey;
    }

    /**
     * Cleanup and disconnect
     */
    disconnect() {
        // Stop fill subscription first
        this.stopFillSubscription();

        // Close WebSocket
        if (this.wsClient) {
            this.wsClient.disconnect();
            this.wsClient = null;
        }

        // Clear clients
        this.exchClient = null;
        this.exchClientCancel = null;
        this.infoClient = null;
        this.transport = null;

        // Clear wallets and keys
        this.wallet = null;
        this.walletCancel = null;
        this.privateKey = null;
        this.privateKeyCancel = null;
        this.publicAddress = null;

        // Clear cached data
        this.assetSzDecimals = undefined;
        this.priceDecimals = undefined;
        this.builderAddress = undefined;
        this.assetIndexCache.clear();
        this.perpDexsCache = null;
        this.metaCache.clear();
        this._spotMetaCache = null;

        // Reset state
        this.isInitialized = false;
        this.isDualKeyMode = false;

        // console.log('✅ Hyperliquid client disconnected');
    }
}

// Export singleton
if (typeof window !== 'undefined') {
    window.BrowserHyperliquidClient = new BrowserHyperliquidClient();
}

export default BrowserHyperliquidClient;
